import { useEffect, useEffectEvent } from "react";

type ModifierKey = "shift" | "ctrl" | "alt" | "meta" | "cmd";
type KeyInput = string | [ModifierKey, string];

const useHotKey = (
  keyInput: KeyInput,
  callback: (event: KeyboardEvent) => void,
  deps: unknown[] = []
) => {
  const onHotKey = useEffectEvent(callback);
  const keyDep = Array.isArray(keyInput) ? keyInput.join("+") : keyInput;

  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      let modifierMatch = true;
      let key: string;
      if (Array.isArray(keyInput)) {
        const [modifier, keyValue] = keyInput;

        key = keyValue;
        switch (modifier.toLowerCase()) {
          case "shift":
            modifierMatch = event.shiftKey;
            break;
          case "ctrl":
            modifierMatch = event.ctrlKey;
            break;
          case "alt":
            modifierMatch = event.altKey;
            break;
          case "meta":
          case "cmd":
            modifierMatch = event.metaKey;
            break;
          default:
            modifierMatch = false;
            break;
        }
      } else {
        key = keyInput;
      }

      if (event.key === key && modifierMatch) {
        onHotKey(event);
      }
    };
    document.addEventListener("keydown", handler);
    return () => {
      document.removeEventListener("keydown", handler);
    };
    // Extra caller deps kept for compatibility; onHotKey must not be listed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keyDep, ...deps]);
};

export default useHotKey;

let platformModifierKey = "ctrl"; // control key
if (
  navigator.platform.indexOf("Mac") === 0 ||
  navigator.platform === "iPhone"
) {
  platformModifierKey = "⌘"; // command key
}

export { platformModifierKey };
